import type { FocusEventHandler } from 'react'

import type { FormControlOptions } from './form-control'
import { useFormControlContext } from './form-control'
import { ariaAttr } from '../../utils/dom'
import { callAllHandlers } from '../../utils/function'

export type UseFormControlProps<T extends HTMLElement> = {
  id?: string
  onFocus?: FocusEventHandler<T>
  onBlur?: FocusEventHandler<T>
  /** Disables the input */
  disabled?: boolean
  /** This prop prevents the user from modifying the value of the input. It is ignored if the value of the type attribute is hidden, range, color, checkbox, radio, file, or a button type (such as button or submit). */
  readOnly?: boolean
  /** Makes the input required and sets aria-invalid to true */
  required?: boolean
} & FormControlOptions

/**
 * React hook that provides the props that should be spread on to
 * input fields (`input`, `select`, `textarea`, etc.).
 *
 * It provides a convenient way to control a form fields, validation
 * and helper text.
 */
export function useFormControl<T extends HTMLElement>(
  props: UseFormControlProps<T>
): {
  disabled: boolean
  readOnly: boolean
  required: boolean
  'aria-invalid': boolean | undefined
  'aria-required': boolean | undefined
  'aria-readonly': boolean | undefined
  'aria-describedby': string | undefined
  id: string
  onFocus: (event: React.FocusEvent<T>) => void
  onBlur: (event: React.FocusEvent<T>) => void
} {
  const { isDisabled, isInvalid, isReadOnly, isRequired, ...rest } =
    useFormControlProps(props)

  return {
    ...rest,
    disabled: isDisabled,
    readOnly: isReadOnly,
    required: isRequired,
    'aria-invalid': ariaAttr(isInvalid),
    'aria-required': ariaAttr(isRequired),
    'aria-readonly': ariaAttr(isReadOnly)
  }
}

export function useFormControlProps<T extends HTMLElement>(
  props: UseFormControlProps<T>
): {
  'aria-describedby': string | undefined
  id: string
  isDisabled: boolean
  isReadOnly: boolean
  isRequired: boolean
  isInvalid: boolean
  onFocus: (event: React.FocusEvent<T>) => void
  onBlur: (event: React.FocusEvent<T>) => void
} {
  const field = useFormControlContext()

  const {
    id,
    disabled,
    readOnly,
    required,
    isRequired,
    isInvalid,
    isReadOnly,
    isDisabled,
    onFocus,
    onBlur,
    ...rest
  } = props

  const labelIds: string[] = []

  // Error message must be described first in all scenarios.
  if (field?.hasFeedbackText && field?.isInvalid) {
    labelIds.push(field.feedbackId)
  }

  if (field?.hasHelpText) {
    labelIds.push(field.helpTextId)
  }

  return {
    ...rest,
    'aria-describedby': labelIds.join(' ') || undefined,
    id: id ?? field?.id,
    isDisabled: disabled ?? isDisabled ?? field?.isDisabled,
    isReadOnly: readOnly ?? isReadOnly ?? field?.isReadOnly,
    isRequired: required ?? isRequired ?? field?.isRequired,
    isInvalid: isInvalid ?? field?.isInvalid,
    onFocus: callAllHandlers(field?.onFocus, onFocus),
    onBlur: callAllHandlers(field?.onBlur, onBlur)
  }
}
